import { ChevronLeftIcon, Save, X } from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import { useNavigate, useLocation } from "react-router-dom";
import { Input, Textarea } from "../../components/ui/input";
import { Checkbox, Select, TimePicker } from "antd";
import { Button } from "../../components/ui/button";
import { useState, useEffect, useCallback } from "react";
import { cn } from "../../utils/tailwindUtils";
import { leaveTypeConfigEnm, leavePolicyExpiryEnum } from "../../constants/enum";
import { EMPLOYEE_LEAVE_POLICY } from "../../constants/api";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import dayjs from "dayjs";

const EmployeeLeavePolicyAddEdit = () => {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        policy_code: "",
        request_type: undefined,
        policy_name: "",
        no_of_days: "",
        selected_time: null,
        leave_period: undefined,
        carry_forward: false,
        notes: "",
        is_active: true,
        company_id: ""
    });

    const { create, update } = useApiMethods();
    const navigate = useNavigate();
    const location = useLocation();
    const { fromEdit, editDetails, isActive } = location.state || {};
    const companyid = sessionStorage.getItem("userId");

    // Initialize form data
    useEffect(() => {
        if (fromEdit && editDetails) {
            setFormData({
                policy_code: editDetails.code || "",
                request_type: editDetails.request_type || undefined,
                policy_name: editDetails.leave_name || "",
                no_of_days: editDetails.no_of_days?.toString() || "",
                selected_time: editDetails.no_of_hours ? dayjs(editDetails.no_of_hours, "HH:mm:ss") : null,
                leave_period: editDetails.leave_expiry || undefined,
                carry_forward: editDetails.leave_carry_forward || false,
                notes: editDetails.notes || "",
                is_active: editDetails.is_active ?? true,
                company_id: editDetails.company_id || companyid
            });
        } else {
            setFormData(prev => ({
                ...prev,
                company_id: companyid
            }));
        }
    }, [fromEdit, editDetails, companyid]);

    const handleBack = () => {
        navigate('/master/leavetype');
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Validation function
    const validateForm = () => {
        if (!formData.request_type) {
            ToastMsg("error", "Request Type is required!");
            return false;
        }

        if (formData.request_type === "leave" && !formData.policy_name) {
            ToastMsg("error", "Leave Policy Name is required!");
            return false;
        }

        if ((formData.request_type === "leave" || formData.request_type === "work_from_home") && !formData.no_of_days) {
            ToastMsg("error", "No of Days is required!");
            return false;
        }

        if (formData.request_type === "permission" && !formData.selected_time) {
            ToastMsg("error", "Select Time is required!");
            return false;
        }

        if (!formData.leave_period) {
            ToastMsg("error", "Leave Period is required!");
            return false;
        }

        return true;
    };

    const handleSave = async (isDraft = false) => {
        if (!isDraft && !validateForm()) {
            return;
        }

        setLoading(true);
        try {
            // Prepare payload
            const payload = {
                code: formData.policy_code || null,
                request_type: formData.request_type,
                leave_name: formData.request_type === 'leave' ? formData.policy_name : null,
                no_of_days: formData.no_of_days ? parseInt(formData.no_of_days) : 0,
                no_of_hours: formData.request_type === 'permission' && formData.selected_time
                    ? formData.selected_time.format("HH:mm:ss")
                    : '00:00:00',
                notes: formData.notes || '',
                leave_expiry: formData.leave_period ? parseInt(formData.leave_period) : null,
                leave_carry_forward: formData.carry_forward,
                is_active: formData.is_active,
                company_id: formData.company_id,
                is_draft: isDraft
            };

            // Add ID for edit mode
            if (fromEdit && editDetails?.id) {
                payload.id = editDetails.id;
            }

            let response;
            if (fromEdit) {
                response = await update(EMPLOYEE_LEAVE_POLICY, payload);
            } else {
                response = await create(EMPLOYEE_LEAVE_POLICY, payload);
            }

            if (response?.status === "success") {
                ToastMsg("success", response.message || `Leave policy ${fromEdit ? 'updated' : 'created'} successfully`);
                handleBack();
            } else {
                ToastMsg("error", response?.message || `Failed to ${fromEdit ? 'update' : 'create'} leave policy`);
            }
        } catch (error) {
            console.error('Error saving leave policy:', error);
            ToastMsg("error", "An error occurred while saving the leave policy");
        } finally {
            setLoading(false);
        }
    };

    const handleSaveAsDraft = useCallback(() => {
        handleSave(true);
    }, [formData]);

    const handleCancel = () => {
        handleBack();
    };

    // Number validation for no_of_days
    const handleNumberInput = (e) => {
        const value = e.target.value;
        if (value === "" || (parseInt(value) >= 1 && parseInt(value) <= 30)) {
            handleInputChange('no_of_days', value);
        }
    };

    const handleKeyDown = (e) => {
        if (
            !/^[\d.]$/.test(e.key) &&
            e.key !== "Backspace" &&
            e.key !== "Delete" &&
            e.key !== "ArrowLeft" &&
            e.key !== "ArrowRight" &&
            e.key !== "Home" &&
            e.key !== "End"
        ) {
            e.preventDefault();
        }
    };

    // Options for dropdowns
    const requestTypeOptions = leaveTypeConfigEnm.map(option => ({
        value: option.value,
        label: option.text
    }));

    const leavePeriodOptions = leavePolicyExpiryEnum.map(option => ({
        value: option.value,
        label: option.text
    }));

    // Check if form has data for draft button
    const hasFormData = Object.values(formData).some(value =>
        value !== "" && value !== undefined && value !== null && value !== false
    );

    return (
        <ContainerCard>
            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
                <ChevronLeftIcon className="h-5 w-5 cursor-pointer" onClick={handleBack} />
                <div className="flex-1">
                    <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {fromEdit ? "Edit Leave Policy" : "Add Leave Policy"}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Define different types of leave and time-off policies
                    </p>
                </div>

                {/* Active Status Toggle */}
                <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                    {/* <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Active Status
                    </label> */}
                    <div className="flex items-center gap-3">
                        <span className={cn(
                            "text-sm font-medium",
                            formData.is_active
                                ? "text-green-600 dark:text-green-400"
                                : "text-gray-500 dark:text-gray-400"
                        )}>
                            {formData.is_active ? "Active" : "Inactive"}
                        </span>
                        <button
                            type="button"
                            onClick={() => handleInputChange('is_active', !formData.is_active)}
                            className={cn(
                                "relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200",
                                formData.is_active ? "bg-sidebar-primary" : "bg-gray-300 dark:bg-gray-600",
                                !fromEdit && !isActive ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                            )}
                            disabled={!fromEdit && !isActive}
                        >
                            <span
                                className={cn(
                                    "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-200",
                                    formData.is_active ? "translate-x-4" : "translate-x-1"
                                )}
                            />
                        </button>
                    </div>
                </div>
            </div>

            {/* Form Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    {/* Leave Policy Code - Only show in edit mode */}
                    {fromEdit && (
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Leave Policy Code <span className="text-red-500">*</span>
                            </label>
                            <Input
                                value={formData.policy_code}
                                onChange={(e) => handleInputChange('policy_code', e.target.value)}
                                placeholder="Enter policy code"
                                className={cn(
                                    "w-full rounded-md transition-colors duration-200",
                                    "border-gray-300 dark:border-gray-600",
                                    "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                    "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                    "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                                )}
                                disabled={fromEdit}
                            />
                        </div>
                    )}

                    {/* Request Type */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Request Type <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={formData.request_type}
                            onChange={(value) => handleInputChange('request_type', value)}
                            options={requestTypeOptions}
                            className="w-full"
                            size="large"
                            placeholder="Select request type"
                            allowClear
                            disabled={fromEdit}
                        />
                    </div>

                    {/* Conditionally render fields based on request type */}
                    {formData.request_type === "leave" && (
                        <>
                            {/* Leave Policy Name */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Leave Policy Name <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    value={formData.policy_name}
                                    onChange={(e) => handleInputChange('policy_name', e.target.value)}
                                    placeholder="Enter policy name"
                                    className={cn(
                                        "w-full rounded-md transition-colors duration-200",
                                        "border-gray-300 dark:border-gray-600",
                                        "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                        "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                        "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                                    )}
                                    disabled={formData.policy_name === "Work From Home"}
                                />
                            </div>

                            {/* No of Days */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Number of Days <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    type="number"
                                    value={formData.no_of_days}
                                    onChange={handleNumberInput}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Enter number of days"
                                    min="1"
                                    max="30"
                                    className={cn(
                                        "w-full rounded-md transition-colors duration-200",
                                        "border-gray-300 dark:border-gray-600",
                                        "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                        "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                        "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                                    )}
                                />
                            </div>
                        </>
                    )}

                    {formData.request_type === "work_from_home" && (
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Number of Days <span className="text-red-500">*</span>
                            </label>
                            <Input
                                type="number"
                                value={formData.no_of_days}
                                onChange={handleNumberInput}
                                onKeyDown={handleKeyDown}
                                placeholder="Enter number of days"
                                min="1"
                                max="30"
                                className={cn(
                                    "w-full rounded-md transition-colors duration-200",
                                    "border-gray-300 dark:border-gray-600",
                                    "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                    "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                    "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                                )}
                            />
                        </div>
                    )}

                    {formData.request_type === "permission" && (
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Select Time <span className="text-red-500">*</span>
                            </label>
                            <TimePicker
                                value={formData.selected_time}
                                onChange={(time) => handleInputChange('selected_time', time)}
                                format="HH:mm"
                                className={cn(
                                    "w-full rounded-md transition-colors duration-200",
                                    "border-gray-300 dark:border-gray-600",
                                    "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                    "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                    "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                                )}
                                placeholder="Select time"
                                size="large"
                            />
                        </div>
                    )}

                    {/* Leave Period */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Leave Period <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={formData.leave_period}
                            onChange={(value) => handleInputChange('leave_period', value)}
                            options={leavePeriodOptions}
                            className="w-full"
                            size="large"
                            placeholder="Select leave period"
                            allowClear
                        />
                    </div>

                    {/* Carry Forward Checkbox - Full width */}
                    <div className="md:col-span-2 flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                        <Checkbox
                            id="carry_forward"
                            checked={formData.carry_forward}
                            onChange={(e) => handleInputChange('carry_forward', e.target.checked)}
                        />
                        <label htmlFor="carry_forward" className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300">
                            Allow Carry Forward to Next Period
                        </label>
                    </div>

                    {/* Notes - Full width */}
                    <div className="md:col-span-2 space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Notes
                        </label>
                        <Textarea
                            value={formData.notes}
                            onChange={(e) => handleInputChange('notes', e.target.value)}
                            placeholder="Enter additional notes or description"
                            rows={4}
                            showCharCount={true}
                            maxLength={500}
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                    </div>
                </div>

                {/* Required Fields Note */}
                <div className="mb-6 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <p className="text-xs text-blue-700 dark:text-blue-300">
                        <span className="text-red-500">*</span> indicates required fields
                    </p>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <Button
                        variant="outline"
                        onClick={handleCancel}
                        disabled={loading}
                        className="flex items-center gap-2"
                    >
                        <X className="w-4 h-4" />
                        Cancel
                    </Button>

                    {/* Draft Button - Only show in create mode */}
                    {!fromEdit && (
                        <Button
                            onClick={handleSaveAsDraft}
                            disabled={loading || !hasFormData}
                            className={cn(
                                "bg-gray-600 hover:bg-gray-700",
                                "text-white transition-colors duration-200",
                                "flex items-center gap-2",
                                (!hasFormData) && "opacity-50 cursor-not-allowed"
                            )}
                        >
                            Save as Draft
                        </Button>
                    )}

                    <Button
                        onClick={() => handleSave(false)}
                        disabled={loading}
                        className={cn(
                            "bg-sidebar hover:bg-sidebar-primary",
                            "dark:bg-blue-600 dark:hover:bg-blue-700",
                            "text-white transition-colors duration-200",
                            "flex items-center gap-2"
                        )}
                    >
                        {loading ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        ) : (
                            <Save className="w-4 h-4" />
                        )}
                        {loading ? "Saving..." : (fromEdit ? "Update" : "Save Policy")}
                    </Button>
                </div>
            </div>
        </ContainerCard>
    );
};

export default EmployeeLeavePolicyAddEdit;